/**
 * A top-bar group (CRM, Outreach, Team): a trigger that opens its routes as a
 * menu – on mouse hover, or by click and keys. The trigger reads active when
 * one of its routes matches; inside the menu only the most specific match is
 * (B2G at `/campaigns/market-access`, not Campaigns as well).
 */
import type { ReactNode } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { clsx } from "clsx";

import { Menu, MenuLink } from "../ui/Menu";
import { useMenuTrigger } from "../ui/useMenuTrigger";

export interface NavLeaf {
  to: string;
  label: string;
  end?: boolean;
  /** Items waiting there; hidden when 0 or unknown. */
  count?: number | null;
}

export function NavMenu({
  label,
  items,
  trigger,
  title,
}: {
  label: string;
  items: NavLeaf[];
  /** Trigger content instead of the plain label (Team: state dot + count). */
  trigger?: ReactNode;
  title?: string;
}) {
  const { pathname } = useLocation();
  const menu = useMenuTrigger({ hover: true });
  const current = items
    .filter((item) => matchPath({ path: item.to, end: item.end ?? false }, pathname))
    .reduce<NavLeaf | null>((best, item) => (best && best.to.length >= item.to.length ? best : item), null);
  const active = items.some((item) => matchPath({ path: item.to, end: false }, pathname));
  return (
    <>
      <button
        {...menu.triggerProps}
        title={title}
        className={clsx("topbar-link", active && "is-active")}
      >
        {trigger ?? label}
        <span className="topbar-caret" aria-hidden>
          ▾
        </span>
      </button>
      <Menu
        open={menu.open}
        onClose={() => menu.setOpen(false)}
        anchorRef={menu.triggerRef}
        id={menu.menuId}
        aria-label={label}
        focus={menu.edge}
        {...menu.panelProps}
      >
        {items.map((item) => (
          <MenuLink key={item.to} to={item.to} end={item.end} active={item === current}>
            {item.label}
            {item.count ? <span className="subnav-count menu-count">{item.count}</span> : null}
          </MenuLink>
        ))}
      </Menu>
    </>
  );
}
